import { Injectable, Signal, WritableSignal, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
    Observable,
    catchError,
    combineLatest,
    debounceTime,
    distinctUntilChanged,
    map,
    of,
    startWith,
    switchMap,
    timeout,
} from 'rxjs';
import { SortOrder, __ } from '../../functions';
import {
    GlobalSearchContext,
    SearchSection,
    SearchSectionStatus,
} from '../models/global-search.model';
import { SEARCH_CONNECTOR, SearchConnector } from '../models/search-connector.model';
import { SearchScope } from '../models/search-scope.model';

/** Milliseconds to wait after the last keystroke before querying the connectors. */
const SEARCH_DEBOUNCE_MS = 250;

/** Milliseconds after which a single connector is considered failed (others keep going). */
// The email-tasks search regularly needs 3-7s against the remote QA database — 5s
// turned slow-but-fine connectors into "Fehler beim Laden"
const SEARCH_TIMEOUT_MS = 15000;

/** localStorage key holding the remembered queries, newest first. */
const RECENT_SEARCHES_STORAGE_KEY = 'immo.global-search.recent-searches';

/** Maximum number of remembered queries. */
const RECENT_SEARCHES_MAX = 8;

/**
 * Reads the persisted recent queries — resilient against missing, foreign, or
 * corrupted storage content (private mode, cleared site data, manual edits).
 *
 * @returns {string[]} The stored queries, newest first.
 */
function readRecentSearches(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];

    return Array.isArray(parsed) ? parsed.filter((entry) => typeof entry === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Orchestrates one palette instance: turns the query signal into per-connector
 * sections that load, succeed, and fail independently of each other.
 *
 * Provided by `GlobalSearchDialogComponent` (not root) so every dialog gets a
 * fresh instance and per-call connector overrides can't leak between palettes.
 */
@Injectable()
export class GlobalSearchService {
  // -----------------------------------------------------------------------------------------------------
  // @ DEPENDENCIES
  // -----------------------------------------------------------------------------------------------------
  private readonly defaultConnectors = inject(SEARCH_CONNECTOR, { optional: true }) ?? [];

  // -----------------------------------------------------------------------------------------------------
  // @ INSTANCE VARIABLES
  // -----------------------------------------------------------------------------------------------------

  /** The raw search input, two-way bound to the combobox. */
  readonly query: WritableSignal<string> = signal('');

  /** The active scope, mutable so chips can be removed by the user. */
  readonly scope: WritableSignal<SearchScope> = signal({});

  /** The last committed queries, newest first — shown while the input is empty. */
  readonly recentSearches: WritableSignal<string[]> = signal(readRecentSearches());

  /** When set, only this connector is queried ("Nur: Aufgaben" type filter). */
  readonly connectorFilter: WritableSignal<{ id: string; label: string } | null> = signal(null);

  private readonly _connectors: WritableSignal<SearchConnector[]> = signal([]);

  /**
   * The per-connector sections for the current query. Emits a fresh array whenever
   * any connector's state changes; superseded queries are cancelled via `switchMap`.
   */
  readonly sections: Signal<SearchSection[]> = toSignal(
    combineLatest([
      toObservable(this.query).pipe(
        map((query) => query.trim()),
        debounceTime(SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
      ),
      toObservable(this.scope),
      toObservable(this._connectors),
      toObservable(this.connectorFilter),
    ]).pipe(
      switchMap(([query, scope, connectors, connectorFilter]) =>
        this.searchAll(
          query,
          scope,
          __.IsNullOrUndefined(connectorFilter)
            ? connectors
            : connectors.filter((connector) => connector.id === connectorFilter.id),
        ),
      ),
    ),
    { initialValue: [] },
  );

  // -----------------------------------------------------------------------------------------------------
  // @ COMPUTED VARIABLES
  // -----------------------------------------------------------------------------------------------------

  /** Whether at least one connector is still loading. */
  readonly isLoading: Signal<boolean> = computed(() =>
    this.sections().some((section) => section.status === SearchSectionStatus.Loading),
  );

  /** Total number of results across all sections. */
  readonly totalCount: Signal<number> = computed(() =>
    this.sections().reduce((sum, section) => sum + section.items.length, 0),
  );

  // -----------------------------------------------------------------------------------------------------
  // @ PUBLIC METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Wires the service to a dialog invocation: applies the per-call connector
   * override (falling back to the global defaults) and the initial scope.
   *
   * @param context - The dialog context supplied by the caller.
   * @returns {void}
   */
  initialize(context: GlobalSearchContext | undefined): void {
    const connectors = context?.connectors ?? this.defaultConnectors;

    this._connectors.set(
      __.Sort([...connectors], (connector) => connector.order, SortOrder.Ascending),
    );
    this.scope.set(context?.scope ?? {});
  }

  /**
   * Remembers a query for the empty-input "Letzte Suchen" list. Called when a result
   * is committed — only queries that actually led somewhere are worth re-offering.
   * Deduplicates case-insensitively, keeps newest first, persists via localStorage.
   *
   * @param query - The query that led to a committed result.
   * @returns {void}
   */
  rememberSearch(query: string): void {
    const trimmed = (query ?? '').trim();

    if (__.IsNullOrUndefinedOrEmpty(trimmed)) {
      return;
    }

    const next = [
      trimmed,
      ...this.recentSearches().filter(
        (candidate) => candidate.toLowerCase() !== trimmed.toLowerCase(),
      ),
    ].slice(0, RECENT_SEARCHES_MAX);

    this.recentSearches.set(next);

    try {
      localStorage.setItem(RECENT_SEARCHES_STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Storage full or unavailable (private mode) — the list simply stays in-memory.
    }
  }

  /**
   * Adds (or replaces) a scope chip and clears the query — the follow-up input
   * searches within the new scope. Used by the connectors' "Als Filter" actions.
   *
   * @param key - The `SearchScope.context` key, e.g. `propertyId`.
   * @param value - The scope value, e.g. the entity id.
   * @param label - The human-readable chip label.
   * @returns {void}
   */
  addScope(key: string, value: unknown, label: string): void {
    const current = this.scope();

    this.scope.set({
      context: { ...(current.context ?? {}), [key]: value },
      labels: { ...(current.labels ?? {}), [key]: label },
    });

    this.query.set('');
  }

  /**
   * Removes a scope chip and re-runs the search without it.
   *
   * @param key - The `SearchScope.context` key to drop.
   * @returns {void}
   */
  removeScope(key: string): void {
    const current = this.scope();

    if (__.IsNullOrUndefined(current.context) || __.IsNullOrUndefined(current.context[key])) {
      return;
    }

    const context = { ...current.context };
    const labels = { ...(current.labels ?? {}) };
    delete context[key];
    delete labels[key];

    this.scope.set({ context, labels });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PRIVATE METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Fans the query out to all connectors. Every connector emits its own section
   * lifecycle (loading → success | error) so a slow or failing source never
   * blanks the others.
   *
   * @param query - The trimmed query.
   * @param scope - The active scope.
   * @param connectors - The connectors to query.
   * @returns {Observable<SearchSection[]>} The combined section states.
   */
  private searchAll(
    query: string,
    scope: SearchScope,
    connectors: SearchConnector[],
  ): Observable<SearchSection[]> {
    if (__.IsNullOrUndefinedOrEmpty(query) || __.IsNullOrUndefinedOrEmpty(connectors)) {
      return of([]);
    }

    return combineLatest(
      connectors.map((connector) => this.searchConnector(connector, query, scope)),
    );
  }

  /**
   * Runs a single connector with timeout + error isolation.
   *
   * @param connector - The connector to query.
   * @param query - The trimmed query.
   * @param scope - The active scope.
   * @returns {Observable<SearchSection>} The connector's section lifecycle.
   */
  private searchConnector(
    connector: SearchConnector,
    query: string,
    scope: SearchScope,
  ): Observable<SearchSection> {
    return connector.search(query, scope).pipe(
      timeout(SEARCH_TIMEOUT_MS),
      map((items): SearchSection => ({ connector, status: SearchSectionStatus.Success, items })),
      catchError(() =>
        of<SearchSection>({ connector, status: SearchSectionStatus.Error, items: [] }),
      ),
      startWith<SearchSection>({ connector, status: SearchSectionStatus.Loading, items: [] }),
    );
  }
}
